import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Popover from '@mui/material/Popover';

export interface HoverLabelTextFieldProps extends Omit<TextFieldProps, 'label' | 'type'> {
  label: React.ReactNode;
  popoverContent?: React.ReactNode;
  /**
   * Set true to open popover programmatically. When undefined, it opens on label hover.
   */
  openPopover?: boolean;
  /**
   * Support custom money type for live-formatting.
   */
  type?: TextFieldProps['type'] | 'money';
  /**
   * When type is 'money', allow and preserve a leading '-' sign. Defaults to true.
   */
  allowNegative?: boolean;
}

/**
 * HoverLabelTextField
 * - Wraps MUI TextField
 * - When mouse hovers over the label AND popoverContent is provided, shows a Popover near the label
 * - If no popoverContent is provided, nothing happens on hover
 */
export function HoverLabelTextField({
  label,
  popoverContent,
  openPopover,
  variant = 'standard',
  ...rest
}: HoverLabelTextFieldProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const hasContent = !!popoverContent;
  const controlled = typeof openPopover === 'boolean';
  const open = controlled ? !!openPopover && hasContent : Boolean(anchorEl) && hasContent;

  const inputLabelRef = React.useRef<HTMLLabelElement | null>(null);

  const handleEnter = () => {
    if (!hasContent) return;
    if (!controlled) setAnchorEl(inputLabelRef.current);
  };
  const handleLeave = () => {
    if (!controlled) setAnchorEl(null);
  };

  // Use raw label; attach hover handlers to InputLabel via InputLabelProps

  // Merge InputLabelProps to allow pointer events on the label (MUI defaults to none)
  const mergedInputLabelProps = {
    ...(rest.InputLabelProps || {}),
    sx: {
      pointerEvents: 'auto',
      cursor: hasContent ? 'help' : undefined,
      ...(rest.InputLabelProps as any)?.sx,
    },
    ref: (node: HTMLLabelElement) => {
      inputLabelRef.current = node;
      const originalRef = (rest.InputLabelProps as any)?.ref;
      if (typeof originalRef === 'function') originalRef(node);
      else if (originalRef && typeof originalRef === 'object') (originalRef as any).current = node;
    },
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
  } as TextFieldProps['InputLabelProps'];

  // Money helpers
  const isMoney = (rest as any)?.type === 'money';
  // Default to allowing negatives for money inputs unless explicitly disabled
  const allowNegative = (rest as any)?.allowNegative !== false;
  const liveFormatMoney = (input: string): string => {
    if (input == null) return '';
    let s = String(input);
    const isNeg = allowNegative && s.trim().startsWith('-');
    s = s.replace(/,/g, '').replace(/[^\d.]/g, '');
    if (s === '') return '';
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      const before = s.slice(0, firstDot + 1);
      const after = s.slice(firstDot + 1).replace(/\./g, '');
      s = before + after;
    }
    let intPart = s;
    let fracPart: string | null = null;
    if (s.includes('.')) {
      const parts = s.split('.');
      intPart = parts[0] || '0';
      fracPart = (parts[1] || '').slice(0, 2);
    }
    intPart = intPart.replace(/^0+(\d)/, '$1');
    if (intPart === '') intPart = '0';
    const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const core = fracPart != null ? `${intWithCommas}.${fracPart}` : intWithCommas;
    return isNeg && core !== '0' && core !== '0.00' ? `-${core}` : core;
  };
  const blurFormatMoney = (input: string): string => {
    const raw = (input || '').replace(/,/g, '').trim();
    if (raw === '') return '';
    const isNeg = allowNegative && raw.startsWith('-');
    const n = Number(isNeg ? raw.slice(1) : raw);
    if (!isFinite(n)) return input;
    const base = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return isNeg ? `-${base}` : base;
  };

  // Intercept onChange/onBlur when type === 'money'
  const { onChange, onBlur, onFocus, value } = rest as any;
  const [focused, setFocused] = React.useState(false);
  const moneyAwareProps = isMoney
    ? {
        // Live-format while focused; show 2-decimal padded when blurred
        value: focused ? liveFormatMoney(String(value ?? '')) : blurFormatMoney(String(value ?? '')),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const formatted = liveFormatMoney(e.target.value);
          const cloned = {
            ...e,
            target: { ...e.target, value: formatted },
            currentTarget: { ...e.currentTarget, value: formatted },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange?.(cloned);
        },
        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
          setFocused(true);
          onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          const formatted = blurFormatMoney(e.target.value);
          const cloned = {
            ...e,
            target: { ...e.target, value: formatted },
            currentTarget: { ...e.currentTarget, value: formatted },
          } as React.FocusEvent<HTMLInputElement>;
          setFocused(false);
          onBlur?.(cloned);
        },
        type: 'text' as const,
      }
    : {};

  return (
    <>
      <TextField {...rest} {...moneyAwareProps} variant={variant} label={label} InputLabelProps={mergedInputLabelProps} />
      <Popover
        id="hover-label-textfield-popover"
        open={open}
        anchorEl={inputLabelRef.current}
        onClose={handleLeave}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{ sx: { p: 1, maxWidth: 360 } }}
      >
        {popoverContent}
      </Popover>
    </>
  );
};
